import { Observable } from "rxjs";
import { IDataStore, DatabaseKey } from "../../repository/IDataStore";
import { IAdminApi } from "./AdminApi";
import { IOrganisation, IUser } from "../../model/ModelTypes";
import * as Raven from "raven-js";
import { IConfig } from "../../IConfig";
import { flatMap, map } from "rxjs/operators";

/**
 * Defines the base of any AdminOperations implemented/to be implemented.
 * It provides necessary operations for any admin operations.
 */
export abstract class BaseAdminOperations {
    protected dataStore: IDataStore;
    protected adminApi: IAdminApi;
    protected organisation: IOrganisation;
    protected organisationCode: string;
    protected config: IConfig;
    protected adminUser: IUser;
    /**
     * Builds an instance of an admin operation according to the provided
     * target class.
     * @param targetClass The target admin operations to build
     * @param dataStore IDataStore instance
     * @param adminApi IAdminApi instance
     * @param organisationCode User's organisation code
     * @param user User instance
     * @param config environment configuration
     */
    static build<T extends BaseAdminOperations>( 
        targetClass: new () => T,
        dataStore: IDataStore, 
        adminApi: IAdminApi, 
        organisationCode: string,
        user: IUser,
        config: IConfig
    ): Observable<T> {
        let organisation: IOrganisation;
        return adminApi.getOrganisationDetails(organisationCode)
        .first()
        .pipe(
            flatMap(
                remoteOrganisation => {
                    organisation = remoteOrganisation;
                    return adminApi.getCoursesDetails(
                        remoteOrganisation.code,
                        user.username.substring(
                            user.username.indexOf("-") + 1
                        ),
                        remoteOrganisation.courses.map(item => item.code)
                    );
                }
            ),
            flatMap(
                courses => {
                    organisation.courses = courses;
                    return dataStore.
                    insertDocument<IOrganisation>(organisation, DatabaseKey.organisation).first();
                }
            ),
            map(
                result => {
                    let operation = new targetClass();
                    operation.adminApi = adminApi;
                    operation.dataStore = dataStore;
                    operation.organisation = organisation;
                    operation.organisationCode = organisationCode;
                    operation.config = config;
                    operation.adminUser = user;
                    operation.onInitializationComplete();
                    return operation;
                }
            )
        );
    }

    abstract observeOrganisationChanges(): Observable<IOrganisation>;
    abstract onInitializationComplete();

    protected reportError(error: Error) {
        if (!this.config.isDebug) {
            Raven.captureException(error);
        }
    }
}


// WEBPACK FOOTER //
// ./src/utility/admin/BaseAdminOperations.ts